import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, Divider, InputAdornment, Typography } from "@mui/material"
import type { FC } from "react"
import { Fragment, useMemo, useRef, useState } from "react"
import { useFieldArray, useForm, useWatch } from "react-hook-form"

import { InventoryProductDescription } from "../../../components"
import { TRANSFER_COST_DECIMAL_PRECISION } from "../constants"
import { type TransferLaidDownCostFormValues, transferLaidDownCostFormSchema } from "../schemas/laidDownCost"
import {
  calculateExtendedLaidDownCostPerUom,
  calculateFreightMiscCost,
  calculateFreightMiscCostPerUom,
  calculateTotalTransferCost,
  calculateTransferCostPerUom,
  toTransferLaidDownCostFormValues,
} from "../utils/laidDownCost"
import { calculateTransferTotals } from "../utils/transfer"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { ProductStatusCell } from "@/components/table"
import { AerosNumericField } from "@/components/UiKit/AerosNumericField"
import { FormCheckbox } from "@/components/UiKit/FormControls/FormCheckbox"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { type Transfer, TransferStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { runWithGuardRef } from "@/utils/forms"

const COST_FIELD_PROPS = {
  prefix: "$" as const,
  thousandSeparator: "," as const,
  decimalScale: TRANSFER_COST_DECIMAL_PRECISION,
  fixedDecimalScale: true,
  allowNegative: false,
} as const

enum FreightMiscField {
  CostPerUom = "COST_PER_UOM",
  Cost = "COST",
}

interface TransferLaidDownCostFormDialogProps {
  transfer: Transfer
  onClose: () => void
  onSubmit: () => void
}

export const TransferLaidDownCostFormDialog: FC<TransferLaidDownCostFormDialogProps> = ({
  transfer,
  onClose,
  onSubmit,
}) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const updatingFieldRef = useRef<FreightMiscField | null>(null)

  // TODO(AK-6102): Use the lot UOM transferred once the backend exposes it; qtyToMove is the closest value available today.
  const totals = useMemo(() => calculateTransferTotals(transfer.products), [transfer.products])

  const form = useForm<TransferLaidDownCostFormValues>({
    resolver: zodResolver(transferLaidDownCostFormSchema),
    defaultValues: toTransferLaidDownCostFormValues(transfer),
    mode: "onChange",
  })

  const {
    control,
    setValue,
    formState: { isDirty, isValid },
  } = form

  const { fields } = useFieldArray({ control, name: "products" })

  const [freightMiscCost, warehousingFees, monthlyStorageAccrual, inboundHandlingCost, outboundHandlingCost] = useWatch(
    {
      control,
      name: [
        "freightMiscCost",
        "warehousingFees",
        "monthlyStorageAccrual",
        "inboundHandlingCost",
        "outboundHandlingCost",
      ],
    }
  )
  const products = useWatch({ control, name: "products" })

  const productById = useMemo(
    () => new Map(transfer.products.map(({ productId, product }) => [productId, product])),
    [transfer.products]
  )

  const totalTransferCost = calculateTotalTransferCost({
    freightMiscCost,
    warehousingFees,
    monthlyStorageAccrual,
    inboundHandlingCost,
    outboundHandlingCost,
  })
  const transferCostPerUom = calculateTransferCostPerUom(totalTransferCost, totals.quantity)

  const handleFreightMiscCostPerUomChange = (value: number | null) => {
    if (updatingFieldRef.current && updatingFieldRef.current !== FreightMiscField.CostPerUom) return

    runWithGuardRef(
      () =>
        setValue("freightMiscCost", calculateFreightMiscCost(value, totals.quantity), {
          shouldDirty: true,
          shouldValidate: true,
        }),
      updatingFieldRef,
      { activeValue: FreightMiscField.CostPerUom, inactiveValue: null }
    )
  }

  const handleFreightMiscCostChange = (value: number | null) => {
    if (updatingFieldRef.current && updatingFieldRef.current !== FreightMiscField.Cost) return

    runWithGuardRef(
      () =>
        setValue("freightMiscCostPerUom", calculateFreightMiscCostPerUom(value, totals.quantity), {
          shouldDirty: true,
          shouldValidate: true,
        }),
      updatingFieldRef,
      { activeValue: FreightMiscField.Cost, inactiveValue: null }
    )
  }

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const handleSubmit = form.handleSubmit(() => {
    // TODO(AK-6102): Call the save mutation and call onSubmit only on success. Saving with isCostComplete set also triggers the per-product upsert product AS400 event (AC2).
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
    onSubmit()
  })

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  return (
    <Dialog open onClose={handleCancel} maxWidth='lg' fullWidth data-testid='transfer.laid-down-cost-form-dialog'>
      <DialogHeader title='View / Edit Extended Laid Down Cost' onClose={handleCancel} />
      <DialogContent className='flex flex-col gap-y-8 p-10'>
        <div className='flex flex-col gap-y-6'>
          <Typography variant='body1' className='font-bold text-gray-700'>
            Transfer Costs
          </Typography>
          <div className='grid grid-cols-12 items-start gap-6'>
            <div className='col-span-6 flex items-start gap-x-2'>
              <FormNumericField
                {...COST_FIELD_PROPS}
                className='flex-1'
                control={control}
                name='freightMiscCostPerUom'
                label='Freight & Misc. (per UoM)'
                onChange={handleFreightMiscCostPerUomChange}
                data-testid='transfer.laid-down-cost-form-dialog.freight-misc-cost-per-uom-field'
              />
              <Typography variant='body2' className='mt-[22px] flex h-10 items-center text-gray-700'>
                =
              </Typography>
              <FormNumericField
                {...COST_FIELD_PROPS}
                className='flex-1'
                control={control}
                name='freightMiscCost'
                label='Freight & Misc. Total'
                onChange={handleFreightMiscCostChange}
                data-testid='transfer.laid-down-cost-form-dialog.freight-misc-cost-field'
              />
            </div>
            <FormNumericField
              {...COST_FIELD_PROPS}
              className='col-span-3'
              control={control}
              name='warehousingFees'
              label='Warehousing Fees'
              data-testid='transfer.laid-down-cost-form-dialog.warehousing-fees-field'
            />
            <FormNumericField
              {...COST_FIELD_PROPS}
              className='col-span-3'
              control={control}
              name='monthlyStorageAccrual'
              label='Monthly Storage Accrual'
              data-testid='transfer.laid-down-cost-form-dialog.monthly-storage-accrual-field'
            />
            <FormNumericField
              {...COST_FIELD_PROPS}
              className='col-span-6'
              control={control}
              name='inboundHandlingCost'
              label='Inbound Handling Costs'
              data-testid='transfer.laid-down-cost-form-dialog.inbound-handling-cost-field'
            />
            <FormNumericField
              {...COST_FIELD_PROPS}
              className='col-span-6'
              control={control}
              name='outboundHandlingCost'
              label='Outbound Handling Costs'
              data-testid='transfer.laid-down-cost-form-dialog.outbound-handling-cost-field'
            />
            <AerosNumericField
              className='col-span-4'
              value={totals.quantity}
              label='Total UoM Transferred'
              disabled
              slotProps={{ input: { endAdornment: <InputAdornment position='end'>{totals.uomName}</InputAdornment> } }}
              data-testid='transfer.laid-down-cost-form-dialog.total-uom-transferred-field'
            />
            <AerosNumericField
              {...COST_FIELD_PROPS}
              className='col-span-4'
              value={totalTransferCost}
              label='Total Transfer Costs'
              disabled
              data-testid='transfer.laid-down-cost-form-dialog.total-transfer-costs-field'
            />
            <AerosNumericField
              {...COST_FIELD_PROPS}
              className='col-span-4'
              value={transferCostPerUom}
              label='Transfer Cost per UoM'
              disabled
              data-testid='transfer.laid-down-cost-form-dialog.transfer-cost-per-uom-field'
            />
          </div>
        </div>
        <Divider />
        <div className='flex flex-col gap-y-6'>
          <Typography variant='body1' className='font-bold text-gray-700'>
            Product Costs
          </Typography>
          <div className='grid grid-cols-[auto_1fr_auto] items-start gap-6'>
            {fields.map((field, index) => {
              const product = productById.get(field.productId)!

              return (
                <Fragment key={field.id}>
                  <ProductStatusCell product={product} className='mt-[14px]' />
                  <InventoryProductDescription product={product} className='mt-[14px]' />
                  <div className='flex items-start gap-x-2'>
                    <div className='w-[152px]'>
                      <FormNumericField
                        {...COST_FIELD_PROPS}
                        control={control}
                        name={`products.${index}.originalCostPerUom`}
                        label='Original Cost per UoM'
                        required
                        size='small'
                        data-testid={`transfer.laid-down-cost-form-dialog.original-cost-per-uom-field-${field.productId}`}
                      />
                    </div>
                    <Typography variant='body2' className='mt-[22px] flex h-10 items-center text-gray-700'>
                      +
                    </Typography>
                    <div className='w-[152px]'>
                      <AerosNumericField
                        {...COST_FIELD_PROPS}
                        value={transferCostPerUom}
                        label='Transfer Cost per UoM'
                        disabled
                        data-testid={`transfer.laid-down-cost-form-dialog.product-transfer-cost-per-uom-field-${field.productId}`}
                      />
                    </div>
                    <Typography variant='body2' className='mt-[22px] flex h-10 items-center text-gray-700'>
                      =
                    </Typography>
                    <div className='w-52'>
                      <AerosNumericField
                        {...COST_FIELD_PROPS}
                        value={calculateExtendedLaidDownCostPerUom(
                          products[index]?.originalCostPerUom ?? null,
                          transferCostPerUom
                        )}
                        label='Extended Laid Down Cost per UoM'
                        disabled
                        data-testid={`transfer.laid-down-cost-form-dialog.extended-laid-down-cost-per-uom-field-${field.productId}`}
                      />
                    </div>
                  </div>
                </Fragment>
              )
            })}
          </div>
          <div className='flex justify-end'>
            <FormCheckbox
              control={control}
              name='isCostComplete'
              label='Product Cost Complete'
              // TODO(AK-6099): Enable for `Received` once `TransferStatus` supports it; `PendingShip` and `InTransit` stay disabled (AK-6102 AC1).
              disabled={transfer.status !== TransferStatus.Completed}
              data-testid='transfer.laid-down-cost-form-dialog.cost-complete-checkbox'
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='transfer.laid-down-cost-form-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!isDirty || !isValid}
          onClick={handleSubmit}
          data-testid='transfer.laid-down-cost-form-dialog.save-button'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
